/**
 * Schema inference: majority-vote column typing with anomaly flagging.
 *
 * Rules (deterministic, documented):
 * - Null-like / empty cells are excluded from voting (counted as missing).
 * - Exact numeric winners (int/float) need a supermajority (>80%)
 *   before we claim the column — coercing a text/code column to a number
 *   is the dangerous direction, so we stay conservative there. A clean
 *   int+float mix widens to float.
 * - Boolean and high-specificity semantic winners (iso-date/uuid/email/url) need only a
 *   simple majority (>50%): random strings almost never look like an email
 *   or a UUID, so 2-of-3 is strong evidence and the odd cell out is an anomaly.
 * - Ties and weak majorities fall back to `string` (everything displays as
 *   text) with `mixed` recording the runner-up for the UI.
 * - `1`/`0` columns become `boolean` only when the header looks flag-like
 *   (is_*, has_*, *_flag, active, enabled, ...); otherwise they stay `int`.
 */
import { detectType, parseDateValue } from './detect';
import { camelCase } from './format';
import type { Anomaly, ColumnSchema, ColumnType, MixedInfo, RowValue } from './types';

const BOOLEAN_NAME_RE = /^(is|has|can|should|was|needs?)_|_(flag|enabled|active|paid|deleted|verified|confirmed)$|^(active|enabled|paid|deleted|verified|subscribed|flag)$/i;

const NUMERIC: ReadonlySet<ColumnType> = new Set(['int', 'float']);
const DATE_NAME_RE = /date|time|day|month|year|created|updated|birth|expir|start|end|_at$|timestamp/i;
const PRIORITY: ColumnType[] = ['boolean', 'int', 'float', 'iso-date', 'uuid', 'email', 'url', 'string'];

function share(count: number, populated: number): number {
  return populated === 0 ? 0 : count / populated;
}

function winner(counts: Map<ColumnType, number>, populated: number): { type: ColumnType; count: number } {
  let best: ColumnType = 'string';
  let bestCount = -1;
  for (const t of PRIORITY) {
    const c = counts.get(t) ?? 0;
    if (c > bestCount) {
      bestCount = c;
      best = t;
    }
  }
  void populated;
  return { type: best, count: Math.max(0, bestCount) };
}

/**
 * Header-aware single-cell classifier: epoch-second strings only count as
 * dates when the header looks date-like (`created`, `*_at`, ...), otherwise
 * they stay plain ints (10-digit IDs must not flip a column to iso-date).
 */
function classify(name: string, raw: string): ColumnType | null {
  const t = detectType(raw);
  if (t === 'int' && /^-?\d{9,10}$/.test(raw.trim()) && DATE_NAME_RE.test(name.trim())) {
    return parseDateValue(raw) !== null ? 'iso-date' : t;
  }
  return t;
}

export function inferColumn(name: string, values: RowValue[]): ColumnSchema {
  let nullCount = 0;
  let emptyCount = 0;
  const populated: string[] = [];
  for (const v of values) {
    if (v === null || v === undefined) {
      nullCount++;
      continue;
    }
    const s = String(v);
    if (s === '') {
      emptyCount++;
      nullCount++;
      continue;
    }
    const t = classify(name, s);
    if (t === null) {
      nullCount++;
      continue;
    }
    populated.push(s);
  }

  const counts = new Map<ColumnType, number>();
  let sawDatetime = false;
  let sawDateOnly = false;

  for (const s of populated) {
    const t = classify(name, s)!;
    counts.set(t, (counts.get(t) ?? 0) + 1);
    if (t === 'iso-date') {
      const p = parseDateValue(s);
      if (p?.kind === 'datetime') sawDatetime = true;
      else sawDateOnly = true;
    }
  }
  const dateKind: 'date' | 'datetime' | null = sawDatetime ? 'datetime' : sawDateOnly ? 'date' : null;

  const n = populated.length;
  const { type: rawWinner, count: bestCount } = winner(counts, n);
  const ratio = share(bestCount, n);

  let type: ColumnType = 'string';
  let mixed: MixedInfo | null = null;
  const intC = counts.get('int') ?? 0;
  const floatC = counts.get('float') ?? 0;
  const nonNumericVotes =
    (counts.get('string') ?? 0) +
    (counts.get('boolean') ?? 0) +
    (counts.get('iso-date') ?? 0) +
    (counts.get('uuid') ?? 0) +
    (counts.get('email') ?? 0) +
    (counts.get('url') ?? 0);
  if (n === 0) {
    type = 'string';
  } else if (nonNumericVotes === 0 && (intC + floatC) / n > 0.8) {
    // Numeric widening: a clean int+float mix (no strings) is a float column.
    type = floatC > 0 ? 'float' : 'int';
  } else if (rawWinner === 'string') {
    type = 'string';
  } else if (NUMERIC.has(rawWinner)) {
    // int/float need a supermajority: coercing a text column to a number
    // is the dangerous direction, so weak numeric majorities stay strings.
    if (ratio > 0.8) type = rawWinner;
    else {
      type = 'string';
      mixed = { type: rawWinner, ratio };
    }
  } else {
    // boolean + high-specificity semantic types need a simple majority.
    if (ratio > 0.5) type = rawWinner;
    else {
      type = 'string';
      mixed = { type: rawWinner, ratio };
    }
  }
  // 1/0 flag hint: int columns with a flag-like header become boolean.
  if (type === 'int' && BOOLEAN_NAME_RE.test(name.trim())) {
    const onlyBinary = populated.every((s) => s.trim() === '1' || s.trim() === '0');
    if (onlyBinary && n > 0) type = 'boolean';
  }

  const isStringWinner = type === 'string';
  const anomalies: Anomaly[] = [];
  if (!isStringWinner) {
    values.forEach((v, i) => {
      if (v === null || v === undefined) return;
      const s = String(v);
      const t = classify(name, s);
      if (t === null) return; // missing, not anomalous
      if (t !== type) anomalies.push({ row: i + 1, value: s, expected: type, actual: t });
    });
  }

  const matching = isStringWinner ? 0 : bestCount;
  const confidence = n === 0 ? 0 : isStringWinner ? 1 : matching / n;

  // min/max/avg over matching cells (numbers compare numerically).
  let min: string | null = null;
  let max: string | null = null;
  let avg: number | null = null;
  const matchCells = populated.filter((s) => classify(name, s) === type);
  const basis = isStringWinner ? [] : matchCells;
  if (basis.length > 0) {
    if (type === 'int' || type === 'float') {
      const nums = basis.map(Number).filter(Number.isFinite);
      if (nums.length > 0) {
        min = String(Math.min(...nums));
        max = String(Math.max(...nums));
        avg = nums.reduce((a, b) => a + b, 0) / nums.length;
      }
    } else {
      const sorted = [...basis].sort();
      min = sorted[0];
      max = sorted[sorted.length - 1];
    }
  }
  const uniqueCount = new Set(populated).size;

  return {
    name,
    key: camelCase(name),
    type,
    confidence,
    anomalyCount: anomalies.length,
    anomalies: anomalies.slice(0, 100),
    nullCount,
    emptyCount,
    uniqueCount,
    min,
    max,
    avg,
    mixed,
    dateKind: type === 'iso-date' ? dateKind : null,
  };
}

export function inferColumns(headers: string[], rows: RowValue[][]): ColumnSchema[] {
  return headers.map((h, c) => inferColumn(h, rows.map((r) => (c < r.length ? r[c] : null))));
}

/** Prefer a clean unique `id`-ish column, else the first unique column. */
export function pickPrimaryKey(columns: ColumnSchema[]): string | null {
  const total = (c: ColumnSchema) => c.nullCount + c.anomalyCount;
  const unique = columns.filter((c) => c.uniqueCount > 1 && c.nullCount === 0 && c.anomalyCount === 0);
  if (unique.length === 0) return null;
  const idLike = unique.find((c) => /^id$|_id$|^uuid$|_uuid$/i.test(c.key));
  if (idLike && total(idLike) === 0) return idLike.name;
  // Highest uniqueness wins; ties prefer the leftmost column.
  let best = unique[0];
  for (const c of unique.slice(1)) {
    if (c.uniqueCount > best.uniqueCount) best = c;
  }
  return best.name;
}
